import React, { useEffect } from 'react';

const DynamicFavicon = () => {
  useEffect(() => {
    const generateFavicons = async () => {
      const environment = process.env.REACT_APP_ENVIRONMENT || 'production';
      const eventName = process.env.REACT_APP_EVENT_NAME || 'default';
      const logoUrl = process.env.REACT_APP_LOGO_URL;
      
      // Configuration des favicons selon l'environnement
      const faviconConfig = {
        'vandb-fest': {
          title: 'VandB Fest #5 - Objets Trouvés',
          shortName: 'VandB Fest #5',
          themeColor: '#1b9faa',
          logoUrl: logoUrl || '/static/logo.svg'
        },
        'sherlook': {
          title: 'Sherlook - Objets Trouvés',
          shortName: 'Sherlook',
          themeColor: '#1b9faa',
          logoUrl: logoUrl || '/static/logo.svg'
        },
        'default': {
          title: 'Objets Trouvés',
          shortName: 'Objets Trouvés',
          themeColor: '#1b9faa',
          logoUrl: logoUrl || '/static/logo.svg'
        }
      };
      
      // Sélectionner la configuration selon l'environnement
      const config = faviconConfig[eventName] || faviconConfig.default;
      
      // Supprimer les anciens favicons
      const existingFavicons = document.querySelectorAll('link[rel*="icon"], link[rel*="apple-touch-icon"]');
      existingFavicons.forEach(link => link.remove());
      
      // Mettre à jour le titre
      document.title = config.title;
      
      // Générer les favicons automatiquement
      await generateFaviconFromLogo(config.logoUrl, config);
      
      // Mettre à jour le theme-color
      updateThemeColor(config.themeColor);
      
      // Mettre à jour le manifest dynamiquement
      updateManifest(config);
    };
    
    generateFavicons();
  }, []);
  
  return null; // Ce composant ne rend rien visuellement
};

// Fonction pour générer les favicons à partir d'un logo
const generateFaviconFromLogo = async (logoUrl, config) => {
  try {
    // Créer un canvas pour générer les différentes tailles
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Charger l'image du logo
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Générer les différentes tailles de favicons
      const sizes = [
        { size: 16, name: 'favicon-16x16' },
        { size: 32, name: 'favicon-32x32' },
        { size: 96, name: 'favicon-96x96' },
        { size: 192, name: 'android-icon-192x192' },
        { size: 512, name: 'android-icon-512x512' },
        { size: 180, name: 'apple-touch-icon' }
      ];
      
      sizes.forEach(({ size, name }) => {
        generateFaviconSize(canvas, ctx, img, size, name, config);
      });
      
      // Générer le favicon.ico (32x32)
      generateFaviconICO(canvas, ctx, img, config);
    };
    
    img.onerror = () => {
      console.warn('Impossible de charger le logo, utilisation des favicons par défaut');
      loadDefaultFavicons(config);
    };
    
    img.src = logoUrl;
  } catch (error) {
    console.error('Erreur lors de la génération des favicons:', error);
    loadDefaultFavicons(config);
  }
};

// Fonction pour générer une taille spécifique de favicon
const generateFaviconSize = (canvas, ctx, img, size, name, config) => {
  canvas.width = size;
  canvas.height = size;
  
  // Effacer le canvas
  ctx.clearRect(0, 0, size, size);
  
  // Dessiner un fond rond avec la couleur du thème
  ctx.fillStyle = config.themeColor;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.fill();
  
  // Calculer les dimensions pour centrer l'image
  const scale = Math.min(size * 0.6 / img.width, size * 0.6 / img.height);
  const scaledWidth = img.width * scale;
  const scaledHeight = img.height * scale;
  const x = (size - scaledWidth) / 2;
  const y = (size - scaledHeight) / 2;
  
  // Dessiner l'image centrée
  ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
  
  // Convertir en blob et créer l'URL
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    createFaviconLink(name, url, size, config);
  }, 'image/png');
};

// Fonction pour générer le favicon.ico
const generateFaviconICO = (canvas, ctx, img, config) => {
  canvas.width = 32;
  canvas.height = 32;
  
  ctx.clearRect(0, 0, 32, 32);
  
  // Fond rond
  ctx.fillStyle = config.themeColor;
  ctx.beginPath();
  ctx.arc(16, 16, 16, 0, 2 * Math.PI);
  ctx.fill();
  
  // Image centrée
  const scale = Math.min(32 * 0.6 / img.width, 32 * 0.6 / img.height);
  const scaledWidth = img.width * scale;
  const scaledHeight = img.height * scale;
  const x = (32 - scaledWidth) / 2;
  const y = (32 - scaledHeight) / 2;
  
  ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
  
  // Convertir en ICO (en fait PNG pour la compatibilité)
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    createFaviconLink('favicon', url, '64x64 32x32 24x24 16x16', config, 'image/x-icon');
  }, 'image/png');
};

// Fonction pour créer un lien favicon
const createFaviconLink = (name, url, sizes, config, type = 'image/png') => {
  const link = document.createElement('link');
  
  if (name === 'favicon') {
    link.rel = 'icon';
    link.type = type;
  } else if (name.includes('apple-touch')) {
    link.rel = 'apple-touch-icon';
  } else if (name.includes('android')) {
    link.rel = 'icon';
  } else {
    link.rel = 'icon';
  }
  
  link.href = url;
  link.sizes = sizes;
  if (type) link.type = type;
  
  document.head.appendChild(link);
};

// Fonction pour utiliser les favicons par défaut
const loadDefaultFavicons = (config) => {
  const defaultIcons = {
    favicon: '/favicon/favicon.ico',
    appleTouch: '/favicon/apple-touch-icon.png',
    android192: '/favicon/web-app-manifest-192x192.png',
    android512: '/favicon/web-app-manifest-512x512.png'
  };
  
  // Créer les liens pour les favicons par défaut
  createFaviconLink('favicon', defaultIcons.favicon, '64x64 32x32 24x24 16x16', config, 'image/x-icon');
  createFaviconLink('apple-touch-icon', defaultIcons.appleTouch, '180x180');
  createFaviconLink('android-icon-192x192', defaultIcons.android192, '192x192');
  createFaviconLink('android-icon-512x512', defaultIcons.android512, '512x512');
};

// Fonction pour mettre à jour le theme-color
const updateThemeColor = (themeColor) => {
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.content = themeColor;
  } else {
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = themeColor;
    document.head.appendChild(meta);
  }
};

// Fonction pour mettre à jour le manifest.json dynamiquement
const updateManifest = (config) => {
  const manifest = {
    short_name: config.shortName,
    name: config.title,
    description: `Plateforme de gestion d'objets trouvés et perdus pour ${config.shortName}. Retrouvez vos objets ou aidez quelqu'un à retrouver les siens.`,
    icons: [
      {
        src: "/favicon/favicon.ico",
        sizes: "64x64 32x32 24x24 16x16",
        type: "image/x-icon"
      },
      {
        src: "/favicon/web-app-manifest-192x192.png",
        sizes: "192x192",
        type: "image/png",
        purpose: "any maskable"
      },
      {
        src: "/favicon/web-app-manifest-512x512.png",
        sizes: "512x512",
        type: "image/png",
        purpose: "any maskable"
      }
    ],
    start_url: ".",
    display: "standalone",
    orientation: "portrait",
    theme_color: config.themeColor,
    background_color: "#ffffff",
    categories: ["utilities", "productivity"],
    lang: "fr",
    dir: "ltr"
  };
  
  // Créer un blob avec le manifest et l'assigner
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
  const manifestUrl = URL.createObjectURL(blob);
  
  // Mettre à jour le lien du manifest
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) {
    manifestLink.href = manifestUrl;
  }
};

export default DynamicFavicon;
